import { IYearMonth } from '@/coreDomainsModule/date';

/**
 * 出力形式の定数
 */
export const OutputFormat = {
  BY_MONTH: 'byMonth',
  BY_DATA_TYPE: 'byDataType',
} as const;

/**
 * 出力形式の型
 */
export type OutputFormat = (typeof OutputFormat)[keyof typeof OutputFormat];

/**
 * 選択可能な項目のベースインターフェース
 */
export interface ISelectableItem {
  key: string;
  label: string;
  checked: boolean;
  selectionOrder?: number;
  /** 単一フィールド項目の挿入位置（月別出力データ項目のキー、この項目の前に挿入） */
  insertBeforeKey?: string;
}

/**
 * マスタ項目
 */
export type IMasterItem = ISelectableItem;

/**
 * 出力データ項目
 */
export type IOutputDataItem = ISelectableItem;

/**
 * プレビューヘッダーのタイプ
 */
export const PreviewHeaderType = {
  MASTER: 'master',
  OUTPUT_DATA: 'outputData',
} as const;

/**
 * プレビューヘッダーのタイプ
 */
export type PreviewHeaderType = (typeof PreviewHeaderType)[keyof typeof PreviewHeaderType];

/**
 * プレビューヘッダー
 */
export interface IPreviewHeader {
  id: string;
  label: string;
  type: PreviewHeaderType;
  masterKey?: string;
  outputDataKey?: string;
  yearMonth?: IYearMonth;
  originalIndex: number;
}

/**
 * マスタ項目かどうかを判定する型ガード
 * @param header - プレビューヘッダー
 * @returns マスタ項目の場合true
 */
export const isMasterHeader = (header: IPreviewHeader): boolean => {
  return header.type === PreviewHeaderType.MASTER && header.masterKey !== undefined;
};

/**
 * 出力データ項目かどうかを判定する型ガード
 * @param header - プレビューヘッダー
 * @returns 出力データ項目の場合true
 */
export const isOutputDataHeader = (header: IPreviewHeader): boolean => {
  return header.type === PreviewHeaderType.OUTPUT_DATA && header.outputDataKey !== undefined;
};

/**
 * Excelエクスポート結果データ
 */
export interface IExportInventoryResultData {
  /** S3からのダウンロード用署名付きURL */
  download_url: string;
  /** ダウンロード用ファイル名 */
  file_name: string;
  /** URLの有効期限（ISO8601形式） */
  expires_at: string;
}

/**
 * 出力条件プリセット（組織内で共有される保存済みの出力条件）
 */
export interface IInventoryTemplatePreset {
  presetId: string;
  name: string;
  masterItemKeys: string[];
  outputDataItemKeys: string[];
  /** 月に紐づかない単一フィールド項目（現在庫など）の挿入位置。キー=項目キー、値=その項目より前にある月別列の数 */
  singleFieldPositions: Record<string, number> | null;
  /** 保存操作時点の今月を基準とした開始月のオフセット（月単位） */
  startMonthOffset: number;
  /** 保存操作時点の今月を基準とした終了月のオフセット（月単位） */
  endMonthOffset: number;
  outputFormat: OutputFormat;
}

/**
 * 出力条件プリセットの登録・更新リクエスト
 */
export interface IInventoryTemplatePresetUpsertRequest {
  name: string;
  masterItems: string[];
  outputDataItems: string[];
  singleFieldPositions: Record<string, number> | null;
  startMonthOffset: number;
  endMonthOffset: number;
  outputFormat: OutputFormat;
}
